import { Alert, Button } from "reactstrap";
import {
  Mission,
  MissionService,
  MissionType,
  SaveTestersConsentService,
  UserService,
} from "../generated";
import React, { Component } from "react";
import { Redirect, RouteComponentProps } from "react-router";

import { AuthorisationStatus } from "../generated/models/VRP/AuthorisationStatus";
import { Policy } from "./policy";
import { VrpCreateConsentRequest } from "../generated/models/VRP/VrpCreateConsentRequest";
import { VrpService } from "../generated/services/VrpService";
import DOMPurify from "dompurify";

export interface State {
  loading: boolean;
  mission: Mission;
  provider: string;
  name: string;
  isInvited: boolean;
  isAuthenticated: boolean;
  area: string;
}

export default class VrpsGetStarted extends Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      loading: true,
      mission: null,
      provider: null,
      name: null,
      isInvited: false,
      isAuthenticated: false,
      area: null,
    };
    this.provideConsent = this.provideConsent.bind(this);
  }
  async componentDidMount() {
    const isAuthenticated = await UserService.userIsAuthenticated();
    if (isAuthenticated) {
      const isInvited = await UserService.userIsInvited();
      this.setState({
        isInvited,
        isAuthenticated,
      });
    }

    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);

    const missionResult = await MissionService.viewMission({
      missionId: sanitizedCode,
    });
    this.setState({
      mission: missionResult,
      loading: false,
      provider: missionResult.providerId,
      name: missionResult.consent.paymentDetails?.debtor?.name,
      area: missionResult.area,
    });
  }

  formatConsentExpiryDate(inputDate: Date) {
    let date, month, year;

    date = inputDate.getDate();
    month = inputDate.getMonth() + 1;
    year = inputDate.getFullYear();

    date = date.toString().padStart(2, "0");

    month = month.toString().padStart(2, "0");

    return `${date}/${month}/${year}`;
  }

  formatConsentRequestDateTime(inputDate: Date) {
    let date, month, year, hour, minute;

    date = inputDate.getDate();
    month = inputDate.getMonth() + 1;
    year = inputDate.getFullYear();
    hour = inputDate.getHours();
    minute = inputDate.getMinutes();
    var ampm = hour >= 12 ? "pm" : "am";
    hour = hour % 12;
    hour = hour ? hour : 12; // the hour '0' should be '12'
    minute = minute < 10 ? "0" + minute : minute;
    date = date.toString().padStart(2, "0");

    month = month.toString().padStart(2, "0");

    return `${date}/${month}/${year} ${hour}:${minute} ${ampm}`;
  }

  renderWelcome() {
    return (
      <div>
        <h2>
          Hi {this.state.name || "there"}
          <br />
        </h2>

        <p>
          We need your permission to set up Variable recurring payments with{" "}
          {this.state.provider} with the following details:
        </p>

        {this.renderPaymentDetails()}

        <p>Please click the button below to provide consent.</p>

        <p>{this.renderPrivacyNotice()}</p>
        <Button color="primary" onClick={this.provideConsent}>
          Provide Consent
        </Button>
      </div>
    );
  }

  renderPrivacyNotice() {
    return (
      <Policy
        area={this.state.area}
        providerId={this.state.provider}
        buttonPosition="bellow"
        missionType={MissionType.VRPCONSENT}
      />
    );
  }

  renderPaymentDetails() {
    let consent = this.state.mission.consent;
    return (
      <Alert color="primary">
        <h4 className="alert-heading">
          Variable Recurring Payments Consent Details
        </h4>
        <hr />
        <dl className="row mb-0">
          <dt className="col-sm-3">Destination Name</dt>
          <dd className="col-sm-9">{consent.paymentDetails.creditor.name}</dd>

          <dt className="col-sm-3">Destination Sort Code</dt>
          <dd className="col-sm-9">
            {consent.paymentDetails.creditor.sortCode}
          </dd>

          <dt className="col-sm-3">Destination Account number</dt>
          <dd className="col-sm-9">
            {consent.paymentDetails.creditor.accountNumber}
          </dd>

          {consent.amountLimits.perPayment && (
            <>
              <dt className="col-sm-3">Max amount per payment</dt>
              <dd className="col-sm-9">
                {consent.amountLimits.perPayment.amount.value.toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 2,
                  }
                )}{" "}
                {consent.amountLimits.perPayment.amount.currency}
              </dd>
            </>
          )}

          {consent.amountLimits.day && (
            <>
              <dt className="col-sm-3">Max amount per day</dt>
              <dd className="col-sm-9">
                {consent.amountLimits.day.amount.value.toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 2,
                  }
                )}{" "}
                {consent.amountLimits.day.amount.currency}
              </dd>
            </>
          )}

          {consent.amountLimits.week && (
            <>
              <dt className="col-sm-3">
                Max amount per week starting at consent creation
              </dt>
              <dd className="col-sm-9">
                {consent.amountLimits.week.amount.value.toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 2,
                  }
                )}{" "}
                {consent.amountLimits.week.amount.currency}
              </dd>
            </>
          )}

          {consent.amountLimits.fortnight && (
            <>
              <dt className="col-sm-3">Max amount per fortnight</dt>
              <dd className="col-sm-9">
                {consent.amountLimits.fortnight.amount.value.toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 2,
                  }
                )}{" "}
                {consent.amountLimits.fortnight.amount.currency}
              </dd>
            </>
          )}

          {consent.amountLimits.halfYear && (
            <>
              <dt className="col-sm-3">Max amount per half year</dt>
              <dd className="col-sm-9">
                {consent.amountLimits.halfYear.amount.value.toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 2,
                  }
                )}{" "}
                {consent.amountLimits.halfYear.amount.currency}
              </dd>
            </>
          )}

          {consent.amountLimits.year && (
            <>
              <dt className="col-sm-3">Max amount per year</dt>
              <dd className="col-sm-9">
                {consent.amountLimits.year.amount.value.toLocaleString(
                  navigator.language,
                  {
                    minimumFractionDigits: 2,
                  }
                )}{" "}
                {consent.amountLimits.year.amount.currency}
              </dd>
            </>
          )}
          <dt className="col-sm-3">Consent Expiry Date</dt>
          <dd className="col-sm-9">
            {this.formatConsentExpiryDate(new Date(consent.validTo))}
          </dd>
          <dt className="col-sm-3">Consent Request Date and Time</dt>
          <dd className="col-sm-9">
            {this.formatConsentRequestDateTime(new Date()) + ""}
          </dd>
        </dl>
      </Alert>
    );
  }

  async provideConsent() {
    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);
    await SaveTestersConsentService.SaveTestersConsent({
      missionId: sanitizedCode,
    });
    const vrpConsentRequest: VrpCreateConsentRequest = {
      consent: this.state.mission.consent,
      endUserContext: null,
      consentContext: null,
      persistentState: null,
    };
    try {
      if (sanitizedCode !== null) {
        const encodedCode = encodeURIComponent(sanitizedCode);
        const consent = await VrpService.GetVrpConsent({
          missionId: encodedCode,
          environment: this.state.mission.executionEnvironment,
          consentRequest: vrpConsentRequest,
        });

        if (consent.status === AuthorisationStatus.In_Progress) {
          window.location.href = consent.redirectUrl;
        } else {
          this.props.history.push("/stage-routing/callback", {
            loading: false,
            success: false,
          });
        }
      }
    } catch {
      this.props.history.push("/stage-routing/callback", {
        loading: false,
        success: false,
      });
    }
  }

  render() {
    const { isAuthenticated, isInvited, loading } = this.state;

    if (loading) {
      return <p>Loading, please wait...</p>;
    }

    if (isAuthenticated && isInvited) {
      return <Redirect to={`/onboarding`} />;
    }

    if (!loading) {
      return this.renderWelcome();
    }
    return (
      <div>
        <h2>Error</h2>
        <br />
        <p>Something went wrong.</p>
      </div>
    );
  }
}
